import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Avatar from "@/components/Avatar";
import ReportButton from "@/components/ReportButton";
import Seo from "@/components/Seo";
import ListenTogether from "@/components/ListenTogether";
import {
  Music2, Plus, Heart, Play, X, Loader2, Sparkles, Star, Radio,
  ListMusic, Volume2, Pause, Trash2, Pencil,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Worship", "Hymns", "Gospel", "Contemporary", "Instrumental"];

/**
 * /music — dedicated Gospel Music page.
 *
 * Layout: a sticky "Now Playing" embed at the top, then category tabs, then
 * the track grid. Clicking any track swaps the embed without leaving the page,
 * so members can browse while a song keeps playing.
 *
 * Admins see extra tools: a "Feature" star, "Set as ambient" badge (sets the
 * one site-wide background track that the AmbientPlayer streams).
 */
export default function Music() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get("category");
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(initial && CATEGORIES.includes(initial) ? initial : "All");
  const [playing, setPlaying] = useState(null); // currently-playing track in the hero embed
  const [openSubmit, setOpenSubmit] = useState(false);
  const [form, setForm] = useState({ title: "", artist: "", youtube_url: "", category: "Worship", description: "" });
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const params = active && active !== "All" ? { category: active } : {};
      const { data } = await api.get("/music", { params });
      setTracks(data);
      if (!playing && data.length) setPlaying(data[0]);
    } catch {
      toast.error("Could not load tracks");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (active === "All") {
      if (searchParams.get("category")) setSearchParams({}, { replace: true });
    } else {
      if (searchParams.get("category") !== active) setSearchParams({ category: active }, { replace: true });
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.youtube_url.trim() || !form.artist.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/music", form);
      toast.success(data.status === "approved"
        ? "Added to the library 🎵"
        : "Submitted — an admin will approve shortly.");
      setForm({ title: "", artist: "", youtube_url: "", category: form.category, description: "" });
      setOpenSubmit(false);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not submit");
    } finally { setBusy(false); }
  };

  const toggleLove = async (t) => {
    try {
      const { data } = await api.post(`/music/${t.id}/love`);
      setTracks((arr) => arr.map((x) => x.id === t.id ? { ...x, loves: data.loves, i_love: data.i_love } : x));
    } catch {}
  };

  const setFeatured = async (t, next) => {
    try {
      const { data } = await api.patch(`/admin/music/${t.id}`, { is_featured: next });
      setTracks((arr) => arr.map((x) => x.id === t.id ? data : x));
      toast.success(next ? "Featured" : "Removed from featured");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const setAmbient = async (t) => {
    try {
      const { data } = await api.patch(`/admin/music/${t.id}`, { is_ambient: true });
      setTracks((arr) => arr.map((x) => ({
        ...x,
        is_ambient: x.id === t.id ? true : false,
        ...(x.id === t.id ? data : {}),
      })));
      toast.success("Set as the site's ambient track");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const deleteTrack = async (t) => {
    if (!window.confirm(`Remove "${t.title}" from the wall? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/music/${t.id}`);
      setTracks((arr) => arr.filter((x) => x.id !== t.id));
      if (playing?.id === t.id) setPlaying(null);
      toast.success("Track removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const [editTrack, setEditTrack] = useState(null);
  const saveEdit = async (patch) => {
    if (!editTrack) return;
    try {
      const { data } = await api.patch(`/admin/music/${editTrack.id}`, patch);
      setTracks((arr) => arr.map((x) => x.id === editTrack.id ? data : x));
      setEditTrack(null);
      toast.success("Track updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const tabs = useMemo(() => ["All", ...CATEGORIES], []);
  const featured = tracks.filter((t) => t.is_featured).slice(0, 3);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Seo
        title="Gospel Music — Jesus Just Us"
        description="A curated wall of worship, hymns, gospel, and instrumental songs that have moved the community. Press play and let the Lord meet you here."
      />
      <div className="overline text-burgundy flex items-center gap-2"><Music2 className="h-3.5 w-3.5" /> Gospel Music</div>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl text-jjblue mt-2 leading-tight">Press play. Let the Lord meet you.</h1>
          <p className="mt-2 text-[var(--jj-text-secondary)] max-w-xl">
            Songs members of this family have leaned on — worship, hymns, gospel, instrumentals.
            Every track is reviewed before it goes on the wall.
          </p>
        </div>
        <button onClick={() => setOpenSubmit(true)} className="btn-primary inline-flex items-center gap-2" data-testid="music-new">
          <Plus className="h-4 w-4" /> Add a song
        </button>
      </div>

      {/* Now playing hero */}
      {playing && (
        <div className="mt-7 jj-card overflow-hidden" data-testid="music-now-playing">
          <div className="aspect-video bg-jjblue">
            <iframe
              key={playing.id}
              src={`https://www.youtube.com/embed/${playing.youtube_id}?autoplay=1&rel=0`}
              title={playing.title}
              className="w-full h-full"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          </div>
          <div className="p-5 flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="overline text-burgundy">{playing.category}</div>
              <div className="font-serif text-2xl text-jjblue truncate">{playing.title}</div>
              <div className="text-sm text-[var(--jj-text-secondary)] truncate">{playing.artist}</div>
              {playing.description && <p className="mt-2 text-sm text-jjblue/85">{playing.description}</p>}
            </div>
            <button
              onClick={() => toggleLove(playing)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition ${playing.i_love ? "bg-burgundy/10 text-burgundy" : "text-[var(--jj-muted)] hover:text-burgundy"}`}
              data-testid={`music-love-now-${playing.id}`}
            >
              <Heart className={`h-4 w-4 ${playing.i_love ? "fill-current" : ""}`} /> {playing.loves}
            </button>
          </div>
        </div>
      )}

      {/* Category tabs */}
      <div className="mt-7 flex gap-1.5 flex-wrap" data-testid="music-tabs">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setActive(t)}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${active === t ? "bg-burgundy text-cream" : "bg-cream-surface text-jjblue/80 hover:text-burgundy"}`}
            data-testid={`music-tab-${t.toLowerCase()}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Featured strip */}
      {featured.length > 0 && active === "All" && (
        <div className="mt-7" data-testid="music-featured-strip">
          <div className="overline text-burgundy flex items-center gap-2"><Star className="h-3.5 w-3.5" /> Featured this week</div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {featured.map((t) => (
              <button
                key={t.id}
                onClick={() => setPlaying(t)}
                className="jj-card overflow-hidden hover:-translate-y-0.5 transition-transform text-left bg-gradient-to-b from-cream to-cream-surface"
                data-testid={`music-featured-${t.id}`}
              >
                <div className="aspect-video bg-jjblue">
                  <img src={`https://i.ytimg.com/vi/${t.youtube_id}/hqdefault.jpg`} alt={t.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="text-[10px] uppercase tracking-wider text-burgundy">{t.category}</div>
                  <div className="font-serif text-lg text-jjblue truncate">{t.title}</div>
                  <div className="text-xs text-[var(--jj-muted)] truncate">{t.artist}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Track grid */}
      <div className="mt-7 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading && (
          <div className="col-span-full text-center text-[var(--jj-muted)] py-10 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && tracks.length === 0 && (
          <div className="col-span-full text-center text-[var(--jj-muted)] py-12 jj-card">
            <ListMusic className="h-7 w-7 mx-auto text-burgundy" strokeWidth={1.5} />
            <div className="mt-2">No tracks here yet. Be the first to share a song that moved you.</div>
          </div>
        )}
        {tracks.map((t) => (
          <div key={t.id} className="jj-card p-3 flex flex-col gap-2" data-testid={`music-card-${t.id}`}>
            <button onClick={() => setPlaying(t)} className="block group" data-testid={`music-play-${t.id}`}>
              <div className="aspect-video bg-jjblue rounded-xl overflow-hidden relative">
                <img src={`https://i.ytimg.com/vi/${t.youtube_id}/hqdefault.jpg`} alt={t.title} className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-jjblue/40">
                  <div className="h-12 w-12 rounded-full bg-cream/95 text-burgundy flex items-center justify-center shadow-lg">
                    <Play className="h-5 w-5 ml-0.5" />
                  </div>
                </div>
                {t.is_ambient && (
                  <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider bg-gold text-jjblue px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    <Radio className="h-3 w-3" /> Ambient
                  </span>
                )}
              </div>
            </button>
            <div className="px-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-burgundy">{t.category}</div>
                  <div className="font-serif text-base text-jjblue truncate">{t.title}</div>
                  <div className="text-xs text-[var(--jj-muted)] truncate">{t.artist}</div>
                </div>
                <ReportButton targetType="music" targetId={t.id} label="" />
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-[var(--jj-muted)]">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Avatar user={t.submitter} size={18} />
                  <span className="truncate">{t.submitter?.name}</span>
                </div>
                <div className="flex items-center gap-1">
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => setFeatured(t, !t.is_featured)}
                        className={`p-1 rounded transition ${t.is_featured ? "text-gold" : "text-[var(--jj-muted)] hover:text-gold"}`}
                        title={t.is_featured ? "Unfeature" : "Feature"}
                        data-testid={`music-feature-${t.id}`}
                      >
                        <Star className={`h-3.5 w-3.5 ${t.is_featured ? "fill-current" : ""}`} />
                      </button>
                      <button
                        onClick={() => setAmbient(t)}
                        className={`p-1 rounded transition ${t.is_ambient ? "text-gold" : "text-[var(--jj-muted)] hover:text-burgundy"}`}
                        title="Set as ambient background track"
                        data-testid={`music-ambient-${t.id}`}
                      >
                        <Radio className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => setEditTrack(t)}
                        className="p-1 rounded text-[var(--jj-muted)] hover:text-gold transition"
                        title="Edit track"
                        data-testid={`music-edit-${t.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => deleteTrack(t)}
                        className="p-1 rounded text-[var(--jj-muted)] hover:text-burgundy transition"
                        title="Delete track"
                        data-testid={`music-delete-${t.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => toggleLove(t)}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full transition ${t.i_love ? "bg-burgundy/10 text-burgundy" : "hover:text-burgundy"}`}
                    data-testid={`music-love-${t.id}`}
                  >
                    <Heart className={`h-3.5 w-3.5 ${t.i_love ? "fill-current" : ""}`} /> {t.loves}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Submit modal */}
      {openSubmit && (
        <div className="fixed inset-0 z-50 bg-jjblue/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpenSubmit(false)}>
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="jj-card w-full max-w-lg p-6 sm:p-7"
            data-testid="music-submit-form"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="overline text-burgundy">Add a song</div>
                <h3 className="font-serif text-2xl text-jjblue mt-1">A track the Lord used in you.</h3>
              </div>
              <button type="button" onClick={() => setOpenSubmit(false)} className="text-[var(--jj-muted)] hover:text-burgundy" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-burgundy">Song title</label>
                  <input className="input-jj mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required maxLength={140} data-testid="music-title" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-burgundy">Artist</label>
                  <input className="input-jj mt-1" value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })} required maxLength={140} data-testid="music-artist" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-burgundy">YouTube link</label>
                <input className="input-jj mt-1" placeholder="https://youtube.com/watch?v=..." value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} required data-testid="music-url" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-burgundy">Category</label>
                <select className="input-jj mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="music-category">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-burgundy">Why this song</label>
                <textarea className="input-jj mt-1 min-h-[80px]" maxLength={480} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="One short line — the family will see it." data-testid="music-desc" />
              </div>
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full mt-5" data-testid="music-submit">
              {busy ? "Submitting…" : (isAdmin ? "Add immediately" : "Submit for review")}
            </button>
          </form>
        </div>
      )}

      {editTrack && <EditTrackDialog track={editTrack} onClose={() => setEditTrack(null)} onSave={saveEdit} categories={CATEGORIES} />}

      {/* Listen-with-the-family pod (only when logged in & on this page) */}
      <ListenTogether currentTrack={playing} onJoin={(t) => setPlaying(t)} />
    </div>
  );
}

function EditTrackDialog({ track, onClose, onSave, categories }) {
  const [title, setTitle] = useState(track.title || "");
  const [artist, setArtist] = useState(track.artist || "");
  const [category, setCategory] = useState(track.category || (categories?.[0] || "worship"));
  const [description, setDescription] = useState(track.description || "");
  const [youtubeUrl, setYoutubeUrl] = useState(track.youtube_url || track.url || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !artist.trim()) { toast.error("Title and artist are required"); return; }
    setBusy(true);
    await onSave({
      title: title.trim(),
      artist: artist.trim(),
      category,
      description: description.trim(),
      youtube_url: youtubeUrl.trim(),
    });
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-jjblue/60 backdrop-blur-sm" data-testid="music-edit-dialog">
      <div className="jj-card w-full max-w-lg p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--jj-muted)] hover:text-burgundy" data-testid="music-edit-close">
          <X className="h-5 w-5" />
        </button>
        <div className="overline text-burgundy">Admin · Edit track</div>
        <h2 className="font-serif text-2xl text-jjblue mt-2">{track.title}</h2>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input className="input-jj" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="music-edit-title" />
          <input className="input-jj" placeholder="Artist" value={artist} onChange={(e) => setArtist(e.target.value)} data-testid="music-edit-artist" />
          <select className="input-jj" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="music-edit-category">
            {(categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input className="input-jj" placeholder="YouTube URL" value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} data-testid="music-edit-url" />
          <textarea className="input-jj" rows={3} placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="music-edit-desc" />
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
            <button disabled={busy} className="btn-primary !py-2 !px-5 text-sm" data-testid="music-edit-submit">{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
